/**
 * Calculates a pricing item’s total gross amount.
 *
 * @param pricingItem The item to price:
 * 	- `units`         – how many units
 * 	- `unitPrice`     – price per unit (net or gross)
 * 	- `unitTaxRate`   – tax rate as a percentage (e.g. 19 for 19%)
 * 	- `isTaxIncluded` – whether `unitPrice` already includes tax (gross). Defaults to false.
 * @returns The total gross amount for all units.
 * @example
 * ```ts
 * const pricingItem = {
 *   units: 4,
 *   unitPrice: 100,
 *   unitTaxRate: 19,
 *   isTaxIncluded: true,
 * };
 * const totalGross = unitPricingToTotalGross(pricingItem);
 * console.log(totalGross); // Output: 400
 * ```
 * @example
 * ```ts
 * const pricingItem = {
 *   units: 4,
 *   unitPrice: 100,
 *   unitTaxRate: 19,
 *   isTaxIncluded: false,
 * };
 * const totalGross = unitPricingToTotalGross(pricingItem);
 * console.log(totalGross); // Output: 476
 * ```
 */
export function unitPricingToTotalGross(pricingItem: {
	units?: number;
	unitPrice?: number;
	unitTaxRate?: number;
	isTaxIncluded?: boolean;
}): number {
	if (!pricingItem) {
		return 0;
	}

	const units =
		pricingItem.units == null ||
		isNaN(pricingItem.units) ||
		pricingItem.units < 0
			? 0
			: pricingItem.units;
	const price =
		pricingItem.unitPrice == null || isNaN(pricingItem.unitPrice)
			? 0
			: pricingItem.unitPrice;
	const rate =
		pricingItem.unitTaxRate == null ||
		isNaN(pricingItem.unitTaxRate) ||
		pricingItem.unitTaxRate < 0
			? 0
			: pricingItem.unitTaxRate;
	const included = pricingItem.isTaxIncluded === true;

	// Calculate total gross for all units
	return included ? units * price : units * price * (1 + rate / 100);
}
